import React, { forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextBox from "components/TextBox";
import ListBox from "components/ComboBox";
import TEAM_SIZES from "./TeamSizes";
import KEY_CHALLENGES from "./KeyChallenges";
import CURRENT_SOLUTIONS from "./CurrentSolutions";
import OUTREACHS from "./Outreachs";
import { RootState } from "store";
import {
  setCurrentSolution,
  setCurrentSolutionOthers,
  setFormErrors,
  setKeyChallenges,
  setKeyChallengesOthers,
  setOutreach,
  setOutreachOthers,
  setTeamSize,
  setReferralCode,
  SignUpState,
} from "store/signUpSlice";
import { preferencesSchema } from "validation/schemas/preferences";
import { FormRef } from "types/form";
import AdminService from "api/Admin/AdminService";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";

import styles from "./PreferencesForm.module.scss";

const PreferencesForm = forwardRef<FormRef>((props, ref) => {
  const dispatch = useDispatch();
  const signUpState: SignUpState = useSelector(
    (state: RootState) => state.signUp,
  );

  const clearErrors = () => {
    if (signUpState.errors?.preferences) {
      dispatch(
        setFormErrors({
          ...signUpState.errors,
          preferences: undefined,
        }),
      );
    }
  };

  const onFormChange =
    <T,>(onChange: (value: T) => void) =>
    (value: T) => {
      clearErrors();
      onChange(value);
    };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      clearErrors();
      const result = preferencesSchema.safeParse({
        outreach: signUpState.outreach,
        outreachOthers: signUpState.outreachOthers,
        currentSolution: signUpState.currentSolution,
        currentSolutionOthers: signUpState.currentSolutionOthers,
        keyChallenges: signUpState.keyChallenges,
        keyChallengesOthers: signUpState.keyChallengesOthers,
        teamSize: signUpState.teamSize,
      });

      if (!result.success) {
        dispatch(
          setFormErrors({
            preferences: result.error.errors.reduce(
              (acc, curr) => {
                acc[curr.path[0]] = curr.message;
                return acc;
              },
              {} as Record<string, string>,
            ),
          }),
        );
        return false;
      }

      // Validate Referral Code
      if (signUpState.referralCode.length > 0) {
        try {
          console.log("Sending req");
          const response = await AdminService.adminReferralExists(
            signUpState.referralCode.trim(),
          );
          if (!response.exists) {
            dispatch(
              setFormErrors({
                preferences: {
                  referralCode: "Unknown referral code, please check again",
                },
              }),
            );
            return false;
          }
        } catch (err) {
          dispatch(
            setFormErrors({
              preferences: {
                referralCode: "Unknown error verifying, please try again",
              },
            }),
          );
          return false;
        }
      }

      dispatch(setFormErrors({}));
      return true;
    },
  }));

  return (
    <div className={styles.form}>
      <div
        className={`${styles.field} ${signUpState.outreach?.value === "Others" && styles.oneColOnly}`}
      >
        <div className={styles.content}>
          <div className={styles.label}>How did you hear about us?</div>
          <ListBox
            lean
            className={styles.textbox}
            selected={signUpState.outreach}
            options={OUTREACHS}
            onChange={onFormChange((value: ComboBoxItem<string>) =>
              dispatch(setOutreach(value)),
            )}
            error={!!signUpState.errors?.preferences?.outreach}
          />
          {signUpState.errors?.preferences?.outreach && (
            <div className={styles.errorText}>
              {signUpState.errors.preferences.outreach}
            </div>
          )}
        </div>
      </div>

      {signUpState.outreach?.value === "Others" && (
        <div className={`${styles.field} ${styles.oneColOnly}`}>
          <div className={styles.content}>
            <div className={styles.label}>Please Specify</div>
            <TextBox
              lean
              className={styles.textbox}
              value={signUpState.outreachOthers}
              onChange={onFormChange((value) =>
                dispatch(setOutreachOthers(value)),
              )}
              error={!!signUpState.errors?.preferences?.outreachOthers}
            />
            {signUpState.errors?.preferences?.outreachOthers && (
              <div className={styles.errorText}>
                {signUpState.errors.preferences.outreachOthers}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className={`${styles.field} ${signUpState.currentSolution?.value === "Others" && styles.oneColOnly}`}
      >
        <div className={styles.content}>
          <div className={styles.label}>Current Solution</div>
          <ListBox
            lean
            className={styles.textbox}
            selected={signUpState.currentSolution}
            options={CURRENT_SOLUTIONS}
            onChange={onFormChange((value: ComboBoxItem<string>) =>
              dispatch(setCurrentSolution(value)),
            )}
            error={!!signUpState.errors?.preferences?.currentSolution}
          />
          {signUpState.errors?.preferences?.currentSolution && (
            <div className={styles.errorText}>
              {signUpState.errors.preferences.currentSolution}
            </div>
          )}
        </div>
      </div>

      {signUpState.currentSolution?.value === "Others" && (
        <div className={`${styles.field} ${styles.oneColOnly}`}>
          <div className={styles.content}>
            <div className={styles.label}>Please Specify</div>
            <TextBox
              lean
              className={styles.textbox}
              value={signUpState.currentSolutionOthers}
              onChange={onFormChange((value) =>
                dispatch(setCurrentSolutionOthers(value)),
              )}
              error={!!signUpState.errors?.preferences?.currentSolutionOthers}
            />
            {signUpState.errors?.preferences?.currentSolutionOthers && (
              <div className={styles.errorText}>
                {signUpState.errors.preferences.currentSolutionOthers}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className={`${styles.field} ${signUpState.keyChallenges?.value === "Others" && styles.oneColOnly}`}
      >
        <div className={styles.content}>
          <div className={styles.label}>Key Challenges</div>
          <ListBox
            lean
            className={styles.textbox}
            selected={signUpState.keyChallenges}
            options={KEY_CHALLENGES}
            onChange={onFormChange((value: ComboBoxItem<string>) =>
              dispatch(setKeyChallenges(value)),
            )}
            error={!!signUpState.errors?.preferences?.keyChallenges}
          />
          {signUpState.errors?.preferences?.keyChallenges && (
            <div className={styles.errorText}>
              {signUpState.errors.preferences.keyChallenges}
            </div>
          )}
        </div>
      </div>

      {signUpState.keyChallenges?.value === "Others" && (
        <div className={`${styles.field} ${styles.oneColOnly}`}>
          <div className={styles.content}>
            <div className={styles.label}>Please Specify</div>
            <TextBox
              lean
              className={styles.textbox}
              value={signUpState.keyChallengesOthers}
              onChange={onFormChange((value) =>
                dispatch(setKeyChallengesOthers(value)),
              )}
              error={!!signUpState.errors?.preferences?.keyChallengesOthers}
            />
            {signUpState.errors?.preferences?.keyChallengesOthers && (
              <div className={styles.errorText}>
                {signUpState.errors.preferences.keyChallengesOthers}
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.field}>
        <div className={styles.content}>
          <div className={styles.label}>Team Size</div>
          <ListBox
            lean
            className={styles.textbox}
            selected={signUpState.teamSize}
            options={TEAM_SIZES}
            onChange={onFormChange((value: ComboBoxItem<string>) =>
              dispatch(setTeamSize(value)),
            )}
            error={!!signUpState.errors?.preferences?.teamSize}
          />
          {signUpState.errors?.preferences?.teamSize && (
            <div className={styles.errorText}>
              {signUpState.errors.preferences.teamSize}
            </div>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.content}>
          <div className={styles.label}>Referral Code</div>
          <TextBox
            lean
            className={styles.textbox}
            value={signUpState.referralCode}
            onChange={(value) => {
              clearErrors();
              dispatch(setReferralCode(value));
            }}
            error={!!signUpState.errors?.preferences?.referralCode}
          />
          {signUpState.errors?.preferences?.referralCode && (
            <div className={styles.errorText}>
              {signUpState.errors.preferences.referralCode}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PreferencesForm.displayName = "PreferencesForm";

export default PreferencesForm;
